/**
 * Turns stored screen-state codes (`L180D`, `GENPOP`, `POP`) into the wording
 * the dashboard's own panels use.
 *
 * An unmapped code falls through to a sentence-cased version of itself rather
 * than to a placeholder.
 */

const MODE = {
  POINT_IN_TIME: "Point in time",
  TREND: "Trended",
  PRE_POST: "Pre / post",
};

const PERIOD = {
  YTD: "Year to date",
  L180D: "Last 180 days",
  L90D: "Last 90 days",
  L30D: "Last 30 days",
  L7D: "Last 7 days",
  DRR: "Date range",
  CUSTOM: "Custom range",
};

const BASE = {
  GENPOP: "Gen Pop",
  FAMILIAR: "Familiar",
  ADRECALL: "Ad recall",
};

const STAT_TEST = {
  NONE: "None",
  POP: "Period-over-Period",
  YOY: "Year-over-Year",
  PTP: "Point-to-Point",
};

const INTERVAL = {
  P_W: "Weekly",
  P_M: "Monthly",
  P_Q: "Quarterly",
  P_Y: "Yearly",
  MV_1: "1-week moving average",
  MV_2: "2-week moving average",
  MV_4: "4-week moving average",
  MV_8: "8-week moving average",
  MV_12: "12-week moving average",
};

const GROUPING = {
  BRAND: "Brand",
  SEGMENT: "Segment",
  MARKET: "Market",
};

const SCORE_TYPE = {
  PERCENTAGE: "Percentage",
  INDEX: "Index",
  RANK: "Rank",
};

const MODULE = {
  equity: "Equity",
  conversion: "Conversion",
  visibility: "Visibility",
  advertising: "Advertising",
  emotional: "Emotional",
  reputation: "Reputation",
  reports: "Reports",
};

/** Sentence case for a code with no entry of its own: BARCODE -> Barcode. */
const sentence = (value) => {
  const text = String(value).replace(/_/g, " ").toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const from = (map) => (value) =>
  value ? map[value] ?? sentence(value) : "";

export const modeLabel = from(MODE);
export const baseLabel = from(BASE);
export const statTestLabel = from(STAT_TEST);
export const intervalLabel = from(INTERVAL);
export const groupingLabel = from(GROUPING);
export const scoreTypeLabel = from(SCORE_TYPE);
export const moduleLabel = (value) =>
  value ? MODULE[String(value).toLowerCase()] ?? sentence(value) : "";
export const chartLabel = from({});

/** A period reads as its name, or as its dates when it has them. */
const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/**
 * `2024-09-25` as a person writes it.
 *
 * Parsed by hand rather than through `Date`, which reads a bare ISO date as
 * UTC midnight and then prints it in local time — a date west of Greenwich
 * comes back a day early.
 */
const day = (iso) => {
  const [y, m, d] = String(iso).split("-").map(Number);
  if (!y || !m || !d) return String(iso);
  return `${d} ${MONTHS[m - 1]} ${y}`;
};

/** A range drops the year from its start when both sit in the same one. */
export const periodLabel = (period) => {
  const { type, startDate, endDate } = period ?? {};
  if (startDate && endDate) {
    const from = day(startDate);
    const to = day(endDate);
    const sameYear = String(startDate).slice(0, 4) === String(endDate).slice(0, 4);
    return `${sameYear ? from.replace(/ \d{4}$/, "") : from} – ${to}`;
  }
  if (!type) return "";
  return PERIOD[type] ?? sentence(type);
};

const CHART = {
  BARCODE: "bar",
  COLUMN: "column",
  LINE: "line",
  BUTTERFLY: "butterfly",
  PERCEPTUAL: "perceptual map",
  DISTRIBUTION: "distribution",
};

/** Up to three names, then a count — a reader does not scan twelve. */
const listOf = (values) => {
  const names = (values || []).filter(Boolean).map(String);
  if (!names.length) return "";
  if (names.length <= 3) {
    return names.length === 1
      ? names[0]
      : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  }
  return `${names.slice(0, 3).join(", ")} and ${names.length - 3} more`;
};

const count = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

/**
 * A period as a *command* states it.
 *
 * The command carries `{preset, start, end}` while the stored screen context
 * carries `{type, startDate, endDate}` — the same fact under two sets of
 * names. Reading one with the other's names returns nothing at all, which is
 * how "Changed the period to" ends up with no period after it.
 */
const commandPeriod = (p) =>
  periodLabel({ type: p.preset, startDate: p.start, endDate: p.end });

/**
 * What a command did, said to the person it happened to.
 *
 * A command name is the contract's vocabulary — `selection.set_time_period`
 * means something to whoever wrote the handler and nothing to whoever watched
 * their screen change. This reads the parameters and reports the actual
 * change, in the past tense, with the real values in it: "Changed the period
 * to Last 180 days", not "Set time period".
 *
 * `resolve` turns ids into names; without it the numbers are simply counted
 * rather than printed, since a raw brand id explains nothing.
 */
export const describeCommand = ({ command, params = {} } = {}, resolve = {}) => {
  const p = params || {};
  // A sentence that trails off ("Changed the period to", "Opened ") is worse
  // than a plain one. An absent value interpolates to nothing, so the tell is
  // simply that the sentence ends in whitespace - more reliable than listing
  // the words it might have ended on.
  const or = (text, fallback) =>
    /\s$/.test(text) || /undefined/.test(text) ? fallback : text;
  const brands = (ids) =>
    listOf((ids || []).map((id) => resolve.brand?.(id)).filter(Boolean)) ||
    count((ids || []).length, "brand");

  switch (command) {
    // ── what is on screen ──────────────────────────────────────────────────
    case "selection.set_module":
      return or(`Opened ${moduleLabel(p.value)}`, "Changed the screen");
    case "selection.set_screen":
      return `Switched to the ${sentence(p.value).toLowerCase()} view`;
    case "selection.apply":
      return "Set up the screen for this question";
    case "selection.reset":
      return "Reset the screen";

    // ── who is being measured ──────────────────────────────────────────────
    case "selection.set_brands":
      return `Changed the brands to ${brands(p.ids)}`;
    case "selection.add_brands":
      return `Added ${brands(p.ids)}`;
    case "selection.remove_brands":
      return `Removed ${brands(p.ids)}`;
    case "selection.set_competitors":
      return `Compared against ${brands(p.ids)}`;
    case "selection.set_markets":
      return `Changed the market to ${
        listOf((p.ids || []).map((id) => resolve.market?.(id)).filter(Boolean)) ||
        count((p.ids || []).length, "market")
      }`;
    case "selection.set_segments":
      return `Applied ${count((p.codes || []).length, "segment")}`;
    case "selection.clear_segments":
      return "Cleared the segments";
    case "selection.set_filters":
      return (p.filters || []).length
        ? `Applied ${count(p.filters.length, "filter")}`
        : "Cleared the filters";
    case "selection.clear_filters":
      return "Cleared the filters";
    case "selection.set_age_range":
      return `Narrowed the audience to ages ${p.min}–${p.max}`;

    // ── how it is measured ─────────────────────────────────────────────────
    case "selection.set_audience_base":
      return or(`Changed the audience to ${baseLabel(p.value)}`, "Changed the audience");
    case "selection.set_stat_test":
      return p.value === "NONE"
        ? "Turned significance testing off"
        : `Set significance testing to ${statTestLabel(p.value)}`;
    case "selection.set_analysis_mode":
      return or(`Switched to ${modeLabel(p.value)}`, "Changed the analysis mode");
    case "selection.set_time_period":
      return or(`Changed the period to ${commandPeriod(p)}`, "Changed the period");
    case "selection.set_comparison_period":
      return or(`Set the comparison period to ${commandPeriod(p)}`, "Set the comparison period");
    case "selection.set_interval":
      return or(`Changed the interval to ${intervalLabel(p.value).toLowerCase()}`, "Changed the interval");
    case "selection.set_grouping":
      return or(`Grouped by ${groupingLabel(p.value).toLowerCase()}`, "Changed the grouping");
    case "selection.set_calculation_method":
      return or(`Changed the calculation to ${sentence(p.value).toLowerCase()}`, "Changed the calculation");
    case "selection.set_trend_metric":
      return or(`Changed the metric to ${sentence(p.key).toLowerCase()}`, "Changed the metric");
    case "selection.set_response":
      return or(`Changed the response to ${sentence(p.key).toLowerCase()}`, "Changed the response");
    case "selection.set_distribution":
      return "Changed the distribution";

    // ── how it looks ───────────────────────────────────────────────────────
    case "selection.set_chart_type":
      return `Changed to a ${CHART[p.value] ?? sentence(p.value).toLowerCase()} chart`;
    case "display.hide_series":
      return `Hid ${count((p.ids || []).length, "series")}`.replace("seriess", "series");
    case "display.show_series":
      return `Showed ${count((p.ids || []).length, "series")}`.replace("seriess", "series");
    case "display.show_all_series":
      return "Showed every series again";
    case "display.set_brand_order":
    case "display.set_market_order":
    case "display.set_segment_order":
      return "Reordered the chart";
    case "display.set_brand_color":
    case "display.set_market_color":
    case "display.set_segment_color":
      return "Recoloured the chart";

    // ── going elsewhere, and housekeeping ──────────────────────────────────
    case "navigation.open_link":
    case "navigation.open_view":
      return "Opened a different screen";
    case "export.start":
    case "export.track":
      return "Started an export";
    case "views.reload_current":
    case "views.refresh":
      return "Refreshed the view";
    default:
      if (String(command).endsWith(".refresh")) {
        const what = String(command).split(".")[0].replace(/_/g, " ");
        return `Refreshed the ${what} list`;
      }
      // Something this build has not been taught to describe. Say plainly
      // that the screen changed rather than printing an identifier at a
      // reader who has no use for it.
      return "Changed the screen";
  }
};

/**
 * Catalog names arrive as they are stored — `EDUCATION - US`, `HOUSEHOLD
 * INCOME`. Shouting them in a panel of sentences reads as an error message.
 *
 * Short all-caps runs are left alone: `US` is not a word that was shouted, and
 * title-casing it to `Us` is worse than leaving it as it was.
 */
export const readable = (name) => {
  if (name !== name.toUpperCase() || !/[A-Z]{4}/.test(name)) return name;
  return name
    .split(/\s+-\s+/)
    .map((part) =>
      part
        .split(/\s+/)
        .map((word) =>
          word.length <= 3 ? word : word.charAt(0) + word.slice(1).toLowerCase(),
        )
        .join(" "),
    )
    .join(" · ");
};

/**
 * A segment's name from the store, falling back to its code.
 *
 * The name is on `label`, not `name` — reading `name` always missed and left
 * every segment showing its code. The code is still the last resort: better a
 * row the reader can match against the payload than a segment left out.
 */
export const segmentLabel = (entities, code) => {
  const entity = entities?.[code] ?? entities?.[String(code).toUpperCase()];
  return entity?.label ? readable(entity.label) : String(code ?? "");
};
